/**
 * Application Configuration
 * -----------------------------------------------------------------------------
 * Handles transformation of ECS data into a strongly typed AppConfiguration object.
 * Only assigns values that match the expected type for each flag.
 */

import type { IEcsData } from "@1js/ecs-client";
import { FEATURE_FLAGS, FeatureFlagTypes } from "./featureFlagRegistry";
import { isValidFlagValue, getDefaultConfig } from "./featureFlagsDefaults";
import type { AppConfiguration } from "./types";
import { logDev as debugLog } from '@/logging/devConsole';

/**
 * Type guard to validate ECS-provided flag values.
 * Ensures the value matches the type defined in the registry.
 */
function isValidFlagEntry<K extends keyof FeatureFlagTypes>(
  flag: K,
  value: unknown
): value is FeatureFlagTypes[K] {
  return isValidFlagValue(flag, value);
}

/**
 * Transforms ECS configuration into a strongly typed AppConfiguration object.
 * Only assigns values that match the expected type for each flag.
 * 
 * @param ecsData - The raw ECS data object
 * @returns The strongly typed application configuration
 */
export function transformEcsDataToAppConfig(ecsData: IEcsData): AppConfiguration {
  // Start with defaults (mutable copy)
  const defaultFlags = getDefaultConfig();
  
  if (!ecsData?.ecsConfiguration) {
    debugLog('[ECS-INFO] No ecsConfiguration in data, using defaults');
    return { featureFlags: defaultFlags };
  }
  
  // Extract FrontlineBYOD configuration
  const frontlineConfig = (ecsData.ecsConfiguration as any)?.FrontlineBYOD || {};
  const ecsFeatureFlags = frontlineConfig?.FeatureFlags || frontlineConfig || {};
  
  // Merge ECS-provided values for known flags
  let appliedCount = 0;
  let warningCount = 0;
  
  for (const flag of FEATURE_FLAGS) {
    const ecsValue = ecsFeatureFlags[flag];
    if (ecsValue !== undefined && isValidFlagEntry(flag, ecsValue)) {
      (defaultFlags as any)[flag] = ecsValue;
      appliedCount++;
    } else if (ecsValue !== undefined) {
      warningCount++;
    }
  }
  
  if (appliedCount > 0) {
    debugLog(`[ECS-INFO] Applied ${appliedCount} valid feature flags from ECS`);
  }
  if (warningCount > 0) {
    debugLog(`[ECS-WARN] Ignored ${warningCount} invalid feature flag values from ECS`);
  }
  
  const config: AppConfiguration = { featureFlags: defaultFlags };
  
  // Critical flags processed successfully
  
  return config;
}

/**
 * Validates that a configuration object has all required flags.
 */
export function validateConfiguration(config: unknown): config is AppConfiguration {
  if (!config || typeof config !== 'object') {
    return false;
  }
  
  const configObj = config as Record<string, unknown>;
  
  if (!configObj.featureFlags || typeof configObj.featureFlags !== 'object') {
    return false;
  }
  
  const featureFlags = configObj.featureFlags as Record<string, unknown>;
  
  // Check that all required flags are present with correct types
  for (const flag of FEATURE_FLAGS) {
    if (!(flag in featureFlags) || !isValidFlagValue(flag, featureFlags[flag])) {
      return false;
    }
  }
  
  return true;
}

/**
 * Merges a partial configuration with defaults.
 * Only applies valid values for known flags.
 */
export function mergeWithDefaults(partialConfig: Partial<AppConfiguration>): AppConfiguration {
  const defaultFlags = getDefaultConfig();
  
  if (partialConfig.featureFlags) {
    for (const flag of FEATURE_FLAGS) {
      const value = partialConfig.featureFlags[flag];
      if (value !== undefined && isValidFlagValue(flag, value)) {
        (defaultFlags as any)[flag] = value;
      }
    }
  }
  
  return { featureFlags: defaultFlags };
}

/**
 * Creates a configuration diff showing what changed.
 */
export function createConfigDiff(
  oldConfig: AppConfiguration,
  newConfig: AppConfiguration
): Array<{
  flag: keyof FeatureFlagTypes;
  oldValue: FeatureFlagTypes[keyof FeatureFlagTypes];
  newValue: FeatureFlagTypes[keyof FeatureFlagTypes];
  changed: boolean;
}> {
  return FEATURE_FLAGS.map(flag => ({
    flag,
    oldValue: oldConfig.featureFlags[flag],
    newValue: newConfig.featureFlags[flag],
    changed: oldConfig.featureFlags[flag] !== newConfig.featureFlags[flag],
  }));
}

/**
 * Checks if two configurations are equal.
 */
export function areConfigurationsEqual(
  config1: AppConfiguration,
  config2: AppConfiguration
): boolean {
  return FEATURE_FLAGS.every(flag => config1.featureFlags[flag] === config2.featureFlags[flag]);
}

/**
 * Get configuration summary for logging/debugging.
 */
export function getConfigurationSummary(config: AppConfiguration): string {
  const summary = FEATURE_FLAGS.map(flag => `${flag}: ${config.featureFlags[flag]}`).join(', ');
  return `{ ${summary} }`;
}